import * as Sentry from "@sentry/browser";
import { Address } from "@wagmi/core";
import { API_BASE_URL } from "config/env";
import Decimal from "decimal.js";
import { merge } from "lodash-es";
import { createAsyncAction, errorResult, successResult } from "pullstate";
import { getIsUserConnected } from "utils/userUtils";
import { DUMMY_WALLET, NUM_TRADES_TO_FETCH } from "../../config/consts";
import { Amount } from "../../types/amount";
import {
  TokenHistoryInfo,
  TokenHistoryInfoResponse,
  TradeHistoryData,
  TradeHistoryDataRaw,
  TransactionDetail,
} from "../../types/history";
import { Token } from "../global/TokenEnum";
import { getTokenDecimals } from "../global/getAllowances";
import { TokenMetadata } from "../global/getTokenMetadata";
import { ActivityStore } from "./ActivityStore";

//api-uat.bebop.xyz/history/trades?wallet_address=0x5Bad996643a924De21b6b2875c85C33F3c5bBcB6&start=1680303600000000000&end=1704067200000000000&size=1&tokens=USDC,USDT,WBTC
export const fetchAllHistory = (
  address: Address,
  tokenMetadata: TokenMetadata,
  timestamp: number,
  abortFetchSignal: AbortSignal
): Promise<TradeHistoryData | Error> => {
  /* The start & end parameters can be quite confusing:
   *   start=0 means start from the oldest possible point
   *   end= Today's timestamp meaning fetch tades from oldest point to now
   * */

  return fetch(
    `${API_BASE_URL}/history/trades?wallet_address=${address}&start=0&end=${timestamp}&size=${NUM_TRADES_TO_FETCH}`,
    { signal: abortFetchSignal }
  )
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        console.error(`Could not fetch trade history. `, response.statusText);
        Sentry.captureException(`Could not fetch trade history`, {
          extra: { error: response },
        });
        throw new Error("Trade history cannot be fetched.");
      }
    })
    .then((json: TradeHistoryDataRaw) => {
      const tradeDataResults: TradeHistoryData["results"] = transformTradeDataTypes(
        json,
        tokenMetadata
      );
      return { results: tradeDataResults, nextAvailableTimestamp: json.nextAvailableTimestamp };
    })
    .catch(error => {
      return error as Error;
    });
};

export const getHistory = createAsyncAction(
  async ({
    address,
    tokenMetadata,
    timestamp,
    previousResult,
  }: {
    address: Address;
    tokenMetadata: TokenMetadata;
    timestamp: number;
    previousResult: TradeHistoryData | undefined;
  }) => {
    ActivityStore.update(s => {
      s.history.isLoading = true;
    });
    try {
      const controller = new AbortController();
      setTimeout(() => {
        controller.abort("History fetching is taking too long.");
      }, 10_000);

      let result: TradeHistoryData | null = null;
      for (let i = 0; i < 2; i++) {
        result = (await fetchAllHistory(
          address,
          tokenMetadata,
          timestamp,
          controller.signal
        )) as TradeHistoryData;
        if (!(result instanceof Error) && result != null) {
          break;
        }
      }
      if (result instanceof Error) {
        throw result;
      }
      if (result == null) {
        throw new Error("Trade history result is returning null or undefined.");
      }

      const results = previousResult
        ? merge(previousResult.results, result.results)
        : result.results;

      const newHistory = {
        results: results,
        nextAvailableTimestamp: result.nextAvailableTimestamp,
      } as TradeHistoryData;

      if (result.nextAvailableTimestamp !== null) {
        await getHistory.run({
          address: address,
          tokenMetadata: tokenMetadata,
          timestamp: result.nextAvailableTimestamp,
          previousResult: newHistory,
        });
      } else {
        return successResult(newHistory);
      }
    } catch (e) {
      console.error(e);
      const error = typeof e === "string" ? new Error(e) : e;
      if (error instanceof Error) {
        ActivityStore.update(s => {
          s.history.error = e as Error;
        });
      }
      Sentry.captureException(`Could not get trade history for network `, { extra: { event: e } });
      return errorResult([], `Could not get trade history for network ${address}`, e);
    }
    Sentry.captureException(`Could not get trade history for network `);
    return errorResult([], `Could not get trade history for network : ${address}`);
  },
  {
    postActionHook: ({ result }) => {
      if (!result.error && getIsUserConnected()) {
        ActivityStore.update(s => {
          s.history.data = result.payload;
          s.history.isLoading = false;
        });
      }
      ActivityStore.update(s => {
        s.history.isLoading = false;
      });
    },
  }
);

export const uint256toAmountType = (
  amount: bigint,
  token: Token,
  tokenMetadata: TokenMetadata
): Amount => {
  const tokenDecimals = getTokenDecimals(token, tokenMetadata);
  const divisor = new Amount((10 ** tokenDecimals).toString());
  return new Amount(amount.toString()).div(divisor);
};

const transformTokens = (
  tokens: TokenHistoryInfoResponse,
  tokenMetaData: TokenMetadata
): TokenHistoryInfo => {
  const parsedTokens: TokenHistoryInfo = {};
  for (const token of Object.keys(tokens)) {
    const tokenInfo = tokens[token as Token];
    parsedTokens[token as Token] = {
      address: tokenInfo?.address ?? DUMMY_WALLET,
      amount: tokenInfo
        ? uint256toAmountType(tokenInfo.amount, token as Token, tokenMetaData)
        : new Amount("0"),
      amountUsd: tokenInfo?.amountUsd ?? new Decimal(0),
    };
  }
  return parsedTokens;
};
export const transformTradeDataTypes = (
  rawData: TradeHistoryDataRaw,
  tokenMetadata: TokenMetadata
) => {
  const result: TradeHistoryData["results"] = {};
  rawData.results.forEach(trade => {
    let volumeUsd: Amount = new Amount("0");
    try {
      volumeUsd = new Amount(trade.volumeUsd?.toString() ?? "0");
    } catch (e) {
      console.error(e);
      Sentry.captureException(`Could not get volumeUsd for ${trade.txHash} - will default to 0.`, {
        extra: { event: e },
      });
    }

    const parsedTrade: TransactionDetail = {
      chain_id: trade.chain_id,
      status: trade.status,
      type: trade.type,
      sender: trade.sender,
      receiver: trade.receiver,
      sellTokens: transformTokens(trade.sellTokens, tokenMetadata),
      buyTokens: transformTokens(trade.buyTokens, tokenMetadata),
      timestamp: new Date(trade.timestamp),
      volumeUsd,
      gasFeeUsd: trade.gasFeeUsd,
      txHash: trade.txHash,
    };
    result[parsedTrade.txHash] = parsedTrade;
  });
  return result;
};
